import React, { useState, useEffect, useRef } from 'react';

/**
 * Composant individuel pour un curseur distant avec animation
 */
function RemoteCursor({ cursor, canvasWidth, canvasHeight, canvasOffsetX, canvasOffsetY }) {
  const [isVisible, setIsVisible] = useState(false);
  
  // Animation d'entrée
  useEffect(() => {
    // Petit délai pour permettre au DOM de se monter
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Convertir les coordonnées normalisées (0-1) en pixels relatifs au canvas
  const pixelX = canvasOffsetX + (cursor.x * canvasWidth);
  const pixelY = canvasOffsetY + (cursor.y * canvasHeight);
  
  const displayName = cursor.user_info 
    ? `${cursor.user_info.first_name} ${cursor.user_info.last_name}`.trim() || 'Utilisateur'
    : 'Utilisateur';
  
  const cursorColor = cursor.user_info?.color || '#4ECDC4';

  return (
    <div
      style={{
        position: 'fixed',
        left: pixelX,
        top: pixelY,
        pointerEvents: 'none',
        zIndex: 9999,
        // Animation de position fluide + fade in
        transition: 'left 0.05s linear, top 0.05s linear, opacity 0.2s ease-out',
        opacity: isVisible ? 0.7 : 0,
        transform: 'translate(0, 0)', // Éviter le décalage
      }}
    >
      {/* Curseur SVG */}
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        style={{
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
        }}
      >
        <path
          d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.48 0 .72-.58.38-.92L5.94 2.84a.5.5 0 0 0-.44.37z"
          fill={cursorColor}
          stroke="#fff"
          strokeWidth="1.5"
        />
      </svg>
      
      {/* Étiquette avec le nom */}
      <div
        style={{
          position: 'absolute',
          left: '14px',
          top: '14px',
          backgroundColor: cursorColor,
          color: '#fff',
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {displayName}
      </div>
    </div>
  );
}

/**
 * Composant wrapper avec animation de sortie
 */
function AnimatedCursor({ userId, cursor, isLeaving, onLeaveComplete, ...props }) {
  const [opacity, setOpacity] = useState(1);
  
  useEffect(() => {
    if (isLeaving) {
      setOpacity(0);
      const timer = setTimeout(() => onLeaveComplete(userId), 200);
      return () => clearTimeout(timer);
    }
  }, [isLeaving, userId, onLeaveComplete]);

  return (
    <div style={{ opacity, transition: 'opacity 0.2s ease-out' }}>
      <RemoteCursor cursor={cursor} {...props} />
    </div>
  );
}

/**
 * Composant principal pour afficher les curseurs des autres utilisateurs
 * 
 * Optimisations:
 * - N'affiche que les curseurs sur la même période
 * - Ne s'affiche pas si nous sommes en mode trajectoire
 * - Filtre les curseurs des utilisateurs en mode trajectoire
 * - Animation d'entrée/sortie
 * - Les données des curseurs hors période sont minimales côté parent
 */
export default function RemoteCursors({ 
  remoteCursors, 
  currentPeriodId,
  canvasWidth,
  canvasHeight,
  canvasOffsetX = 0,
  canvasOffsetY = 0,
  currentViewMode = 'scene',
  currentTime = null,
  periods = []
}) {
  // État pour gérer les curseurs en cours de disparition (animation)
  const [leavingCursors, setLeavingCursors] = useState({});
  const prevCursorsRef = useRef({});

  // Condition de visibilité d'un curseur
  const isCursorVisible = (cursor) => 
    cursor.period_id === currentPeriodId && cursor.view_mode !== 'trajectory';

  // Détecter les curseurs qui quittent la période
  useEffect(() => {
    const prevVisible = Object.entries(prevCursorsRef.current)
      .filter(([_, c]) => isCursorVisible(c))
      .map(([id]) => id);
    
    const nowVisible = Object.entries(remoteCursors)
      .filter(([_, c]) => isCursorVisible(c))
      .map(([id]) => id);
    
    // Curseurs qui étaient visibles mais ne le sont plus
    const leaving = prevVisible.filter(id => !nowVisible.includes(id));
    
    if (leaving.length > 0) {
      const leavingData = {};
      leaving.forEach(id => {
        if (prevCursorsRef.current[id]) {
          leavingData[id] = prevCursorsRef.current[id];
        }
      });
      setLeavingCursors(prev => ({ ...prev, ...leavingData }));
    }
    
    prevCursorsRef.current = remoteCursors;
  }, [remoteCursors, currentPeriodId]);

  const handleLeaveComplete = (userId) => {
    setLeavingCursors(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  // Déterminer si on est actuellement dans une période (pas en interpolation)
  const isInPeriod = currentTime !== null && periods.some(period => 
    period.start_time !== null && 
    period.end_time !== null && 
    currentTime >= period.start_time && 
    currentTime <= period.end_time
  );

  // Ne pas afficher les curseurs si nous sommes en mode trajectoire ou en interpolation (hors période)
  if (currentViewMode === 'trajectory' || !isInPeriod) {
    return null;
  }

  // Filtrer les curseurs pour n'afficher que ceux sur la même période
  // ET exclure les curseurs des utilisateurs en mode trajectoire
  const visibleCursors = Object.entries(remoteCursors).filter(
    ([userId, cursor]) => 
      cursor.period_id === currentPeriodId && 
      cursor.view_mode !== 'trajectory'
  );

  // Curseurs en train de partir (animation de sortie)
  const leavingEntries = Object.entries(leavingCursors);

  if (visibleCursors.length === 0 && leavingEntries.length === 0) return null;

  const sharedProps = { canvasWidth, canvasHeight, canvasOffsetX, canvasOffsetY };

  return (
    <>
      {/* Curseurs actifs */}
      {visibleCursors.map(([userId, cursor]) => (
        <RemoteCursor
          key={userId}
          cursor={cursor}
          {...sharedProps}
        />
      ))}
      
      {/* Curseurs en train de disparaître */}
      {leavingEntries.map(([userId, cursor]) => (
        <AnimatedCursor
          key={`leaving-${userId}`}
          userId={userId}
          cursor={cursor}
          isLeaving={true}
          onLeaveComplete={handleLeaveComplete}
          {...sharedProps}
        />
      ))}
    </>
  );
}
